<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                User Exposure by Segment
                            </h2>
                        </div>
                    </div>

                    <!-- Denied state -->
                    <zen-denied-state
                        v-if="!canView"
                        title="User exposure by segment restricted"
                        message="User exposure by segment needs a higher plan — no sales call, just upgrade in Billing."
                    />

                    <template v-if="canView">
                        <div
                            class="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                        >
                            <div>
                                <label
                                    for="segment-exposure-filter-from"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    From
                                </label>
                                <input
                                    id="segment-exposure-filter-from"
                                    type="date"
                                    v-model="filters.startDate"
                                    data-testid="segment-exposure-filter-from"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                />
                            </div>
                            <div>
                                <label
                                    for="segment-exposure-filter-to"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    To
                                </label>
                                <input
                                    id="segment-exposure-filter-to"
                                    type="date"
                                    v-model="filters.endDate"
                                    data-testid="segment-exposure-filter-to"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                />
                            </div>
                            <div>
                                <label
                                    for="segment-exposure-filter-flag"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Flag
                                </label>
                                <select
                                    id="segment-exposure-filter-flag"
                                    v-model="filters.flagKey"
                                    data-testid="segment-exposure-filter-flag"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All flags</option>
                                    <option
                                        v-for="flag in flags"
                                        :key="flag.key"
                                        :value="flag.key"
                                    >
                                        {{ flag.name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <zen-button
                                    variant="secondary"
                                    size="field"
                                    data-testid="segment-exposure-apply-filters"
                                    @click="applyFilters"
                                >
                                    Apply
                                </zen-button>
                            </div>
                        </div>

                        <zen-loading-indicator v-if="loading" />

                        <zen-message-block v-if="error" type="error">
                            {{
                                error.message ||
                                'Failed to load user exposure by segment. Please try again.'
                            }}
                        </zen-message-block>

                        <template v-if="!loading">
                            <div
                                class="rounded-md border border-taupe-200 bg-white px-3 py-2 text-sm"
                                data-testid="segment-exposure-total"
                            >
                                <span class="text-taupe-500"
                                    >Total exposures:</span
                                >
                                <span class="font-medium text-taupe-800">{{
                                    totalExposures.toLocaleString()
                                }}</span>
                            </div>

                            <div
                                class="rounded-md border border-taupe-200 bg-white p-4"
                                data-testid="segment-exposure-breakdown"
                            >
                                <zen-empty-table-message
                                    v-if="segments.length === 0"
                                    compact
                                    data-testid="segment-exposure-empty"
                                    icon="ZenIconFilter"
                                >
                                    No segments defined for this environment.
                                </zen-empty-table-message>

                                <ul v-else class="divide-y divide-taupe-100">
                                    <li
                                        v-for="segment in segments"
                                        :key="segment.id"
                                        class="py-3"
                                        :data-testid="
                                            'segment-exposure-row-' + segment.id
                                        "
                                    >
                                        <div
                                            class="flex items-center justify-between text-sm"
                                        >
                                            <div
                                                class="flex items-center space-x-2"
                                            >
                                                <span
                                                    class="font-medium text-taupe-800"
                                                    >{{ segment.name }}</span
                                                >
                                                <span
                                                    v-if="segment.zero_exposure"
                                                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800"
                                                    data-testid="segment-exposure-zero-badge"
                                                >
                                                    Zero exposure
                                                </span>
                                            </div>
                                            <div class="text-taupe-500">
                                                <span
                                                    class="font-medium text-taupe-800"
                                                    >{{
                                                        segment.total.toLocaleString()
                                                    }}</span
                                                >
                                                ({{ segment.percentage }}%)
                                            </div>
                                        </div>
                                        <div
                                            class="mt-1.5 h-1.5 w-full rounded-full bg-taupe-100"
                                        >
                                            <div
                                                class="h-1.5 rounded-full bg-sky-500"
                                                :style="{
                                                    width:
                                                        segment.percentage +
                                                        '%',
                                                }"
                                            />
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </template>

                        <zen-pagination
                            v-if="!loading"
                            :page="pagination.page"
                            :total-pages="pagination.total_pages"
                            :total="pagination.total"
                            @change="changePage"
                        />

                        <div v-if="!loading" class="text-sm pb-10">
                            Report Generated:
                            <zen-date-time :value="generatedAt" />
                        </div>
                    </template>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import ReportService from '@/services/report.service'
import FlagService from '@/services/flag.service'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { usePlan } from '@/composables/usePlan'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import { useServerPagination } from '@/composables/useServerPagination'

export default {
    name: 'UserExposureBySegment',
    setup() {
        const { current } = useCurrentEnvironment()
        const { hasPlanFeature } = usePlan()
        const { loading, error, run } = useCancelableLoad()
        const { pagination, setPage, resetPage, applyResponse, resetOnError } =
            useServerPagination({ perPage: 50 })
        return {
            current,
            hasPlanFeature,
            loading,
            error,
            run,
            pagination,
            setPage,
            resetPage,
            applyResponse,
            resetOnError,
        }
    },
    data() {
        return {
            totalExposures: 0,
            segments: [],
            flags: [],
            generatedAt: null,
            filters: {
                startDate: '',
                endDate: '',
                flagKey: '',
            },
        }
    },
    computed: {
        canView() {
            return this.hasPlanFeature('advanced_reporting')
        },
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
    },
    watch: {
        environmentScopeKey() {
            this.refresh()
        },
    },
    methods: {
        defaultDateRange() {
            const end = new Date()
            const start = new Date()
            start.setDate(start.getDate() - 29)

            return {
                startDate: start.toISOString().slice(0, 10),
                endDate: end.toISOString().slice(0, 10),
            }
        },
        applyFilters() {
            this.resetPage()
            this.load()
        },
        changePage(page) {
            this.setPage(page)
            this.load()
        },
        load() {
            if (!this.canView) return

            this.run(
                (signal) =>
                    ReportService.getUserExposureBySegment(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            startDate: this.filters.startDate,
                            endDate: this.filters.endDate,
                            flagKey: this.filters.flagKey || undefined,
                            page: this.pagination.page,
                            perPage: this.pagination.per_page,
                        },
                        { signal }
                    ),
                (response) => {
                    this.totalExposures = response.data.total_exposures
                    this.segments = response.data.segments
                    this.generatedAt = response.data.generated_at
                    if (this.applyResponse(response.data.pagination)) {
                        this.load()
                    }
                },
                () => {
                    this.totalExposures = 0
                    this.segments = []
                    this.resetOnError()
                }
            )
        },
        refresh() {
            this.applyFilters()

            if (!this.canView) return

            FlagService.list(this.current.projectKey)
                .then((response) => {
                    this.flags = response.data ?? []
                })
                .catch(() => {
                    this.flags = []
                })
        },
    },
    mounted() {
        Object.assign(this.filters, this.defaultDateRange())
        this.refresh()
    },
}
</script>
